import type { LegacyWorkspaceSummary } from '~/services/LegacyEnterpriseServer/types'

const TEN_THOUSAND_WORDS = BigInt(10000)
const ZERO_WORDS = BigInt(0)

function integer(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
}

/** Never round large quotas through Number or turn a failed request into zero. */
export function formatLegacyWords(value: unknown): string {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) return '—'
  const words = BigInt(value)
  if (words >= TEN_THOUSAND_WORDS && words % TEN_THOUSAND_WORDS === ZERO_WORDS) {
    return `${words / TEN_THOUSAND_WORDS}万`
  }
  return words.toLocaleString('en-US')
}

export function legacyWorkspaceQuota(summary: LegacyWorkspaceSummary | null, site: 'AI' | 'VIP'): string | null {
  const space = Array.isArray(summary?.spaces) ? summary.spaces.find(item => item?.site === site) : null
  if (space?.quotaStatus !== 'AVAILABLE') return null
  const values = [space.serviceWords, space.fuelWords, space.totalWords]
  if (!values.every(value => typeof value === 'string' && /^\d+$/.test(value))) return null
  if (BigInt(space.serviceWords!) + BigInt(space.fuelWords!) !== BigInt(space.totalWords!)) return null
  return space.totalWords!
}

export function legacyWorkspaceQuotaUsage(summary: LegacyWorkspaceSummary | null, site: 'AI' | 'VIP') {
  const space = Array.isArray(summary?.spaces) ? summary.spaces.find(item => item?.site === site) : null
  if (legacyWorkspaceQuota(summary, site) === null || typeof space?.totalQuotaWords !== 'string'
    || typeof space.usedWords !== 'string' || !/^\d+$/.test(space.totalQuotaWords) || !/^\d+$/.test(space.usedWords)) return null
  const total = BigInt(space.totalQuotaWords)
  const used = BigInt(space.usedWords)
  const remaining = BigInt(space.totalWords!)
  if (total < remaining || used !== total - remaining) return null
  return {
    total: space.totalQuotaWords,
    used: space.usedWords,
    remaining: space.totalWords!,
    packageRemaining: space.fuelWords!,
  }
}

export function legacyWorkspaceSeats(summary: LegacyWorkspaceSummary | null) {
  const seats = summary?.seats
  if (seats?.status !== 'AVAILABLE' || !integer(seats.usedSeats) || !integer(seats.seatLimit)
    || !integer(seats.availableSeats) || seats.availableSeats !== Math.max(0, seats.seatLimit - seats.usedSeats)) return null
  if (!Array.isArray(seats.spaces) || seats.spaces.some(space => !space || !['AI', 'VIP'].includes(space.site)
    || !integer(space.usedSeats) || !integer(space.pendingSeats) || !integer(space.reservedSeats)
    || space.pendingSeats + space.reservedSeats > space.usedSeats)
    || new Set(seats.spaces.map(space => space.site)).size !== seats.spaces.length
    || seats.spaces.reduce((sum, space) => sum + space.usedSeats, 0) !== seats.usedSeats) return null
  return seats
}

export function legacyChinaPoolBanner(summary: LegacyWorkspaceSummary | null, loading = false) {
  const words = legacyWorkspaceQuota(summary, 'VIP')
  return words !== null ? `中国版空间已开通（中国池 ${formatLegacyWords(words)}字符）`
    : `中国版空间已开通（中国池余额${loading ? '加载中…' : '暂不可用'}）`
}

/** Instance-scoped request gate: no polling, no cross-SSR state, stale identities are discarded. */
export function createLegacyWorkspaceLoader(
  fetch: () => Promise<{ code: string; data?: LegacyWorkspaceSummary }>,
  apply: (value: LegacyWorkspaceSummary | null) => void,
  busy: (value: boolean) => void,
) {
  let identity = '', migrationNo = '', site = '', revision = 0
  let pending: Promise<void> | undefined
  return {
    reset(next: string, migration = '', currentSite = '') {
      if (identity === next && migrationNo === migration && site === currentSite) return
      identity = next; migrationNo = migration; site = currentSite; revision++; pending = undefined
      apply(null); busy(false)
    },
    refresh(): Promise<void> {
      if (!identity) return Promise.resolve()
      if (pending) return pending
      const current = revision
      busy(true)
      pending = (async () => {
        try {
          const response = await fetch()
          if (current !== revision) return
          const value = response.code === '1' ? response.data : null
          apply(value?.migrationNo === migrationNo && value.currentSite === site
            && value.regionMode === 'CROSS_REGION' && Array.isArray(value.spaces) ? value : null)
        } catch { if (current === revision) apply(null) }
        finally { if (current === revision) { pending = undefined; busy(false) } }
      })()
      return pending
    },
    dispose() { identity = ''; revision++; pending = undefined; apply(null); busy(false) },
  }
}
